/**
 * 视频 / 配音节点面板（刀C，框架骨架）。
 * - VideoNodePanel：展示/编辑本镜自动拼装的 8 层视频 prompt + 生成 + 首帧预览。
 * - AudioNodePanel：展示角色台词 + 生成配音（TTS）+ 播放。
 * 只负责渲染与回调，写回 store 由编辑器负责。生成均为 mock，接真时换实现。
 */
import type { AudioLine, AudioPayload, CanvasNode } from '../../model/canvas';
import { EMOTIONS } from '../../model/voice';
import type { VideoToolOp } from './generationService';

const field = 'w-full rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none';
const genBtn = 'font-semibold text-accent hover:underline disabled:opacity-50';
const vToolBtn =
  'rounded-control border border-surface-line bg-surface-base px-2 py-0.5 text-uiSm text-ink-secondary transition-colors hover:border-accent hover:text-accent disabled:opacity-50';

const VIDEO_TOOLS: { op: VideoToolOp; label: string }[] = [
  { op: 'frame', label: '选帧' },
  { op: 'trim', label: '剪辑' },
  { op: 'upscale', label: '高清' },
  { op: 'desub', label: '去字幕' },
  { op: 'translate', label: '翻译' },
  { op: 'separate', label: '音画分离' },
];

interface VideoNodePanelProps {
  node: CanvasNode;
  generating: boolean;
  onPrompt: (prompt: string) => void;
  onGenerate: () => void;
  onTool?: (op: VideoToolOp) => void;
}

export function VideoNodePanel({ node, generating, onPrompt, onGenerate, onTool }: VideoNodePanelProps) {
  const p = node.params;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-uiSm text-ink-faint">分镜视频 · {p?.model ?? ''} · {p?.resolution ?? ''} · {p?.durationSec ?? 0}s</div>
      <textarea
        value={p?.prompt ?? ''}
        placeholder="8 层视频 prompt（已按本镜自动拼装，可手改；接真实 LLM 时逐层完善）"
        className={`${field} h-40 shrink-0 resize-none`}
        onChange={(e) => onPrompt(e.target.value)}
      />
      <div className="flex shrink-0 items-center gap-2 text-ui">
        <button type="button" className={genBtn} disabled={generating} onClick={onGenerate}>
          {generating ? '生成中…' : '生成分镜视频'}
        </button>
      </div>
      {node.media !== undefined && (
        <div className="overflow-hidden rounded-control border border-surface-line">
          <img src={node.media.thumbnail ?? node.media.url} alt={node.title} className="block w-full" />
        </div>
      )}
      {node.media !== undefined && onTool !== undefined && (
        <div className="flex flex-wrap gap-1.5">
          {VIDEO_TOOLS.map((t) => (
            <button key={t.op} type="button" className={vToolBtn} disabled={generating} onClick={() => onTool(t.op)}>
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface AudioNodePanelProps {
  payload: AudioPayload;
  generating: boolean;
  /** 按角色名解析生效音色名（音频卡继承角色绑定） */
  voiceNameFor: (role: string) => string;
  onPatchLine: (index: number, patch: Partial<AudioLine>) => void;
  onGenerateAll: () => void;
}

const lineSel = 'rounded-control bg-surface-base px-1.5 py-1 text-uiSm text-ink-primary outline-none';

export function AudioNodePanel({ payload, generating, voiceNameFor, onPatchLine, onGenerateAll }: AudioNodePanelProps) {
  const lines = payload.lines;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-uiSm">
        <span className="text-ink-faint">配音 · {lines.length} 句</span>
        <button type="button" className={`${genBtn} ml-auto`} disabled={generating || lines.length === 0} onClick={onGenerateAll}>
          {generating ? '合成中…' : '生成全部配音'}
        </button>
      </div>
      {lines.length === 0 && <p className="text-uiSm text-ink-faint">本镜无台词，无需配音。</p>}
      {lines.map((line, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-control border border-surface-line p-2">
          <div className="flex items-center gap-2">
            <input
              value={line.role}
              className={`${lineSel} w-20`}
              title="角色（回溯其绑定音色）"
              onChange={(e) => onPatchLine(i, { role: e.target.value })}
            />
            <select
              className={lineSel}
              value={line.emotion}
              title="逐句情绪锚点"
              onChange={(e) => onPatchLine(i, { emotion: e.target.value })}
            >
              {EMOTIONS.map((em) => (
                <option key={em} value={em}>
                  {em}
                </option>
              ))}
            </select>
            <span className="ml-auto truncate text-uiSm text-ink-faint" title="音频卡继承所属角色绑定的音色">音色：{voiceNameFor(line.role)}</span>
          </div>
          <textarea
            value={line.text}
            placeholder="台词内容…"
            className={`${field} min-h-12 shrink-0 resize-none`}
            onChange={(e) => onPatchLine(i, { text: e.target.value })}
          />
          {line.url !== undefined && <audio controls src={line.url} className="w-full" />}
        </div>
      ))}
    </div>
  );
}
